<template>
  <Teleport to="body">
    <div class="howto-backdrop" @click.self="close">
      <div class="howto" role="dialog" aria-label="How to play">
        <button class="howto__close" aria-label="Close" @click="close">✕</button>

        <div class="howto__body">
          <h3>{{ slide.title }}</h3>

          <div v-if="slide.cards" class="howto__cards">
            <img
              v-for="value in slide.cards"
              :key="value"
              :src="`${base}card-art/${value}.jpg`"
              :alt="`The ${value} card`"
              class="howto__card"
            >
          </div>

          <!-- eslint-disable-next-line vue/no-v-html -- content is this file's own
               constant strings below, never user input -->
          <p v-for="(line, i) in slide.lines" :key="i" class="howto__line" v-html="line" />
        </div>

        <div class="howto__nav">
          <button class="secondary" :disabled="index === 0" @click="index -= 1">Back</button>
          <span class="howto__dots">
            <i
              v-for="(s, i) in slides"
              :key="i"
              class="howto__dot"
              :class="{ 'howto__dot--on': i === index }"
            />
          </span>
          <button v-if="index < slides.length - 1" class="primary howto__next" @click="index += 1">
            Next
          </button>
          <button v-else class="primary howto__next" @click="close">
            Got it
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { LOWEST_CARD, HIGHEST_CARD } from '@/engine/constants';

// The walkthrough. Matt: friends he invites have often never played, and they
// should be able to "get a sense of the lay of the land when they first
// arrive" without waiting for him to teach it. Same pattern as incan-gold's
// HowToPlay - slides, real card art, three ways out.
//
// The run slide is the whole reason this game needs a tutorial at all: "only
// the lowest card of a run counts" is the one rule that makes No Thanks! a
// game rather than a chore, and it is exactly the rule a first-timer plays
// three rounds without discovering.
const SLIDES = [
  {
    title: 'The worst auction ever',
    cards: [3, 19, 35],
    lines: [
      'The deck is the numbers 3 to 35, and every card is worth its number in <strong>penalty points</strong>. Lowest score wins.',
      'Nine cards were secretly removed before the game, so you never know exactly what\'s out there.',
      'One card is face up at a time, and somebody has to end up with it.',
    ],
  },
  {
    title: 'Take it, or pay',
    lines: [
      'On your turn, two choices:',
      '<strong>Take the card</strong> — it\'s yours, points and all, along with every counter sitting on it. Then you flip the next card, <strong>and it\'s still your turn</strong>.',
      '<strong>Say "no thanks"</strong> — pay one of your counters onto the card and pass it left.',
      'Counters are worth a point each at the end, so paying hurts a little — and when you\'re out of counters, you <strong>must</strong> take the card.',
    ],
  },
  {
    title: 'The trick: runs',
    cards: [15, 16, 17],
    lines: [
      'If you hold cards that run in sequence, <strong>only the lowest one counts against you</strong>.',
      'Holding 15 and 17 costs you 32 points. Add the 16 and the whole run costs just 15.',
      'So a "terrible" card can be a gift — and everyone can see your cards, so they know which cards you want. That\'s where the fun lives.',
    ],
  },
  {
    title: 'Reading the table',
    lines: [
      'Everyone\'s taken cards are face up — watch what your neighbours are collecting.',
      'A card nobody wants goes round and round, growing a pile of counters. Sometimes it\'s worth grabbing just for the pile.',
      'Opponents\' counter counts are hidden, but watch for anyone running <strong>low</strong> — they\'ll be forced to take something soon.',
      'When the deck runs out, lowest total of card points minus counters wins. Good luck.',
    ],
  },
];

export default {
  name: 'HowToPlay',
  emits: ['close'],
  data() {
    return { index: 0, slides: SLIDES, base: process.env.BASE_URL || '/' };
  },
  computed: {
    slide() { return this.slides[this.index]; },
  },
  created() {
    // A slide referencing a card outside the deck would ship a broken image;
    // checked at mount in dev and in the component test.
    if (process.env.NODE_ENV !== 'production') {
      for (const slide of SLIDES) {
        for (const value of slide.cards || []) {
          if (value < LOWEST_CARD || value > HIGHEST_CARD) {
            throw new Error(`HowToPlay slide "${slide.title}" references card ${value}, outside ${LOWEST_CARD}-${HIGHEST_CARD}`);
          }
        }
      }
    }
  },
  methods: {
    close() { this.$emit('close'); },
  },
};
</script>
